import { ref } from "vue"

// Color scheme handling, shared across the app so it works regardless of
// whether the ChangeTheme menu (hidden on mobile) is mounted.
//
// "auto" follows the device's prefers-color-scheme setting and reacts live to
// OS changes. "light"/"dark" pin the scheme explicitly.

export type ColorScheme = "light" | "dark" | "auto"

const STORAGE_KEY = "babble/colorscheme"

const darkMediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

function getStored(): ColorScheme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === "light" || stored === "dark" || stored === "auto") return stored
  return "auto"
}

// Reactive, shared selection used by the ChangeTheme control.
export const colorScheme = ref<ColorScheme>(getStored())

// Reactive flag for whether dark is currently applied (resolves "auto").
// Used for the dark/light badge cue on the ChangeTheme button.
export const isDark = ref(false)

// Whether dark should currently be applied given the chosen scheme.
function effectiveDark(scheme: ColorScheme) {
  return scheme === "dark" || (scheme === "auto" && darkMediaQuery.matches)
}

function applyColorScheme() {
  const dark = effectiveDark(colorScheme.value)
  isDark.value = dark
  document.documentElement.setAttribute("data-bs-theme", dark ? "dark" : "light")
  document.body.classList.toggle("dark", dark)
  document.querySelector("html")?.classList.toggle("p-dark", dark)
}

export function setColorScheme(scheme: ColorScheme) {
  colorScheme.value = scheme
  localStorage.setItem(STORAGE_KEY, scheme)
  applyColorScheme()
}

// Call once at app startup to apply the stored scheme and keep "auto" in sync
// with the OS setting.
export function initColorScheme() {
  applyColorScheme()
  darkMediaQuery.addEventListener("change", () => {
    if (colorScheme.value === "auto") applyColorScheme()
  })
}
